import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  ElectricPlan,
  ElectricPlanResponse,
  ElectricPlanState,
  FilterOptions,
} from "./types/electric-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn } from "./types/types";

const initialState: ElectricPlanState = {
  electricPlans: [],
  electricPlan: null,
  selectedElectricPlan: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  filters: {},
  filterOptions: null,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// ✅ Create DataPlan
export const createElectricPlan = createAsyncThunk<
  ElectricPlanResponse,
  any,
  { rejectValue: ApiError; state: RootState }
>("electricPlans/create", async (ElectricPlanData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(
      `${API_URL}/electric-plans`,
      ElectricPlanData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          // "Content-Type": "application/json",
        },
      }
    );
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data plan" }
    );
  }
});

// ✅ Fetch DataPlans (Paginated)
export const fetchElectricPlans = createAsyncThunk<
  ElectricPlanResponse & PaginationReturn,
  any | { url: string },
  { rejectValue: ApiError; state: RootState }
>("electricPlans/fetch", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/electric-plans`;
    const res = await axios.get(apiUrl, {
      params: urlFilters,
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data plans" }
    );
  }
});

// ✅ Fetch Single DataPlan by ID
export const fetchElectricPlanById = createAsyncThunk<
  ElectricPlan,
  number,
  { rejectValue: ApiError; state: RootState }
>("electricPlans/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/electric-plans/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data plan" }
    );
  }
});

// ✅ Delete DataPlan
export const deleteElectricPlan = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("electricPlans/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/electric-plans/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete data plan" }
    );
  }
});

// ✅ Update DataPlan
export const updateElectricPlan = createAsyncThunk<
  ElectricPlan,
  { id: number; data: Partial<ElectricPlan> | FormData },
  { rejectValue: ApiError; state: RootState }
>("electricPlans/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(
      `${API_URL}/electric-plans/${id}?_method=PUT`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update data plan" }
    );
  }
});

// ✅ Fetch Filter Options
export const fetchFilterOptions = createAsyncThunk<
  FilterOptions,
  void,
  { rejectValue: ApiError; state: RootState }
>("electricPlans/fetchFilterOptions", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/electric-plans/filter-options`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch filter options" }
    );
  }
});

// ✅ Slice
const electricPlanSlice = createSlice({
  name: "electricPlans",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    resetElectricPlanState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedElectricPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createElectricPlan.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createElectricPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.electricPlan) {
          state.electricPlans.unshift(action.payload.electricPlan);
        }
      })
      .addCase(createElectricPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create data plan";
      })

      // Fetch All
      .addCase(fetchElectricPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchElectricPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.electricPlans = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchElectricPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch data plans";
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })

      // Fetch One
      .addCase(fetchElectricPlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedElectricPlan = null;
      })
      .addCase(fetchElectricPlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedElectricPlan = action.payload;
      })
      .addCase(fetchElectricPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch data plan";
      })

      // Delete
      .addCase(deleteElectricPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteElectricPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.electricPlans = state.electricPlans.filter(
          (plan) => plan.id !== action.payload
        );
      })
      .addCase(deleteElectricPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete data plan";
      })

      // Update
      .addCase(updateElectricPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateElectricPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Update data plan in list
        state.electricPlans = state.electricPlans.map((plan) =>
          plan.id === action.payload.id ? action.payload : plan
        );

        // Update selected data plan if it's the one updated
        if (state.selectedElectricPlan?.id === action.payload.id) {
          state.selectedElectricPlan = action.payload;
        }
      })
      .addCase(updateElectricPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update data plan";
      });
  },
});

export const { setFilters, clearFilters, resetElectricPlanState } =
  electricPlanSlice.actions;
export default electricPlanSlice.reducer;
